import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { getArticleSchema, getFAQSchema, EDITORIAL_AUTHOR_ORG } from '../utils/seoSchemas';
import { getGuideBySlug } from '../content/guides/articles';
import GuideRelatedLinks from '../components/GuideRelatedLinks';
import { buildPlannerHref } from '../utils/plannerLink';
import { PrebuiltItineraryBlog } from '../components/PrebuiltItineraryBlog';
import { PREBUILT_ITINERARIES } from '../content/prebuiltItineraries/generated';
import { GuideHero } from '../components/guides/GuideHero';
import { GuideLayoutShell, GuideMainColumn, GuideAsideColumn } from '../components/guides/GuideLayoutShell';
import { GuidePitchDivider } from '../components/guides/GuidePitchDivider';
import { GuideRichSection } from '../components/guides/GuideRichSection';
import { GuideSidebar } from '../components/guides/GuideSidebar';
import { getGuideLayoutDefaults } from '../utils/guideLayoutDefaults';

const GuideArticle = () => {
  const { slug } = useParams();
  const article = getGuideBySlug(slug);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Guide not found</h1>
        <Link to="/guides" className="text-amber-600 dark:text-amber-400 hover:underline">
          Back to guides
        </Link>
      </div>
    );
  }

  const layout = getGuideLayoutDefaults(article);
  const schema = [
    getArticleSchema({
      headline: article.title,
      description: article.description,
      datePublished: article.updated,
      dateModified: article.updated,
      author: EDITORIAL_AUTHOR_ORG,
    }),
    getFAQSchema(article.faq || []),
  ];

  const sections = article.sections || [];

  return (
    <>
      <SEOHead title={`${article.title} | WorldCup Fan Planner`} description={article.description} schema={schema} ogType="article" />
      <div className="bg-background min-h-screen">
        {layout.heroImage ? (
          <GuideHero
            heroImage={layout.heroImage}
            heroImageAlt={layout.heroImageAlt}
            heroBadge={layout.heroBadge}
            h1={article.h1}
            heroSubtitle={layout.heroSubtitle}
            heroAccent={layout.heroAccent}
            updated={article.updated}
          />
        ) : (
          <div className="max-w-7xl mx-auto px-4 pt-10 pb-4">
            <p className="text-sm text-on-surface-variant mb-2">
              <Link to="/guides" className="font-bold text-primary hover:underline">
                Guides
              </Link>
              <span aria-hidden> · </span>
              Updated {article.updated}
            </p>
            <h1 className="text-3xl sm:text-4xl font-black text-on-surface dark:text-white font-headline mb-4">{article.h1}</h1>
          </div>
        )}

        <GuideLayoutShell>
          <GuideMainColumn>
            {sections.map((sec, idx) => {
              if (sec.type === 'prebuilt_itinerary' && sec.prebuiltBundleKey) {
                const plans = PREBUILT_ITINERARIES[sec.prebuiltBundleKey];
                const cityRow = plans?.['3'] || plans?.['5'] || plans?.['7'];
                const fallbackCity = cityRow?.plannerCity || cityRow?.city || sec.prebuiltBundleKey;
                const guideCityName = article.itineraryGuideDisplayName || fallbackCity;
                return (
                  <React.Fragment key={`prebuilt-${sec.prebuiltBundleKey}-${idx}`}>
                    {idx > 0 ? <GuidePitchDivider /> : null}
                    <PrebuiltItineraryBlog guideCityName={guideCityName} plansByDuration={plans} />
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={`sec-${idx}-${sec.title || sec.type || 'block'}`}>
                  {idx > 0 ? <GuidePitchDivider /> : null}
                  <GuideRichSection section={sec} sectionIndex={idx} />
                </React.Fragment>
              );
            })}

            {!article.plannerCta ? (
              <div className="mt-10 p-6 rounded-2xl bg-primary-container/25 dark:bg-primary-dim/20 border border-primary/20">
                <h2 className="text-lg font-semibold text-on-surface dark:text-white mb-2 font-headline">Build your itinerary</h2>
                <p className="text-sm text-on-surface-variant dark:text-slate-400 mb-4">
                  Open the AI itinerary planner with suggested settings—you can change every field before generating.
                </p>
                <Link
                  to={buildPlannerHref(article.plannerCta)}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-primary text-on-primary font-bold text-sm hover:bg-primary-dim transition-colors"
                >
                  Open AI itinerary planner
                </Link>
              </div>
            ) : null}

            <GuidePitchDivider />
            <section
              className="p-6 sm:p-8 rounded-2xl bg-surface-container-low dark:bg-slate-800/80 border border-outline-variant/20 dark:border-slate-700"
              aria-labelledby="guide-faq"
            >
              <h2 id="guide-faq" className="text-xl font-black text-on-surface dark:text-white mb-6 font-headline">
                FAQ
              </h2>
              <dl className="space-y-5">
                {(article.faq || []).map(({ question, answer }) => (
                  <div key={question}>
                    <dt className="font-bold text-on-surface dark:text-white font-headline">{question}</dt>
                    <dd className="text-sm text-on-surface-variant dark:text-slate-300 mt-2 leading-relaxed">{answer}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <div className="mt-10">
              <GuideRelatedLinks
                relatedGuideSlugs={article.relatedGuideSlugs}
                relatedCitySlugs={article.relatedCitySlugs}
                relatedStadiumSlugs={article.relatedStadiumSlugs}
              />
            </div>
          </GuideMainColumn>

          <GuideAsideColumn>
            <GuideSidebar
              plannerCta={article.plannerCta}
              sidebarTips={layout.sidebarTips}
              sidebarWarnings={layout.sidebarWarnings}
              ctaTitle={article.itineraryGuideDisplayName ? `Plan ${article.itineraryGuideDisplayName}` : 'Build your itinerary'}
            />
          </GuideAsideColumn>
        </GuideLayoutShell>
      </div>
    </>
  );
};

export default GuideArticle;
